/// <amd-module name="Release/Landing/HubContent/Releases/NameColumn" />
import * as React from "react";

import { css } from "VSSUI/Util";
import { Link } from "VSSUI/Link";
import { Tooltip } from "VSSUI/TooltipEx";

import { Icon } from "VSSUI/Icon";
import { IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { localeFormat } from "VSS/Core/Util/String";

import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { Release, ReleaseStatus, Artifact } from "Release/Client/Contracts/Release";
import { ActiveReleasesStore, FocusHoverEvent } from "./ActiveReleasesStore";
import { hasRmUIPermissions, ReleaseManagementUISecurityPermissions } from "Release/Helpers/Permission";
import { createUrlForRoute } from "Release/Helpers/Link";
import { getPrimaryArtifact, getPrimaryArtifactBranchName, getArtifactBuildInfo, getArtifactVersionUrl, getArtifactIcon, getBranchDisplayValue, ArtifactTypes } from "Artifact";
import * as Resources from "Release/Landing/Resources";

import "VSS/LoaderPlugins/Css!RM:Release/Landing/HubContent/Releases/NameColumn";

export interface IActiveReleaseNameProps extends IVssComponentProperties {
    release: Release;
    releaseClass: string;
    isDeleted: boolean;
    columnWidth: number;
    artifactNameMaxWidth: number;
    branchNameMaxWidth: number;
    onDesiredReleaseFound?: () => void;
}

interface IActiveReleaseNameState {
    focusedRowReleaseId: number | undefined;
    hoveredRowReleaseId: number | undefined;
}

const avatarImageWidth: number = 50;

export class NameColumn extends VssComponent<IActiveReleaseNameProps, IActiveReleaseNameState> {

    public constructor(props: IActiveReleaseNameProps, context: IVssComponentContext) {
        super(props);
        this.state = { focusedRowReleaseId: -1, hoveredRowReleaseId: -1 };
        this._store = StoreManager.GetStore<ActiveReleasesStore>(ActiveReleasesStore, "active-releases-store", context.pageContext)
        this._store.addListener(FocusHoverEvent, this._onStoreUpdate);
    }

    public componentWillMount(): void {
        this._setElementWidthsFromProps(this.props);
    }

    public componentWillUpdate(): void {
        this._setElementWidthsFromProps(this.props);
    }

    public componentWillReceiveProps(nextProps: IActiveReleaseNameProps) {
        this._setElementWidthsFromProps(nextProps);
    }

    public componentWillUnmount(): void {
        this._store.removeListener(FocusHoverEvent, this._onStoreUpdate);
    }

    public render(): JSX.Element {
        let release = this.props.release as Release;

        let containerStyle;
        if (this._artifactNameMaxWidth + this._branchNameMaxWidth > 200) {
            // For larger branch and artifact names, set max-width to avoid rendering extra space between branch and action(...) button
            containerStyle = {
                "maxWidth": this._releaseDetailsContainerWidth
            };
        }
        else {
            // For small branch and artifact names, set fixed width to name column so that branch name is not cut by action(...) button
            containerStyle = {
                "width": this._releaseDetailsContainerWidth
            };
        }

        return (
            <div className={"active-release-name-column flex"} >

                {this._getReleaseCreatorDetails(release)}

                <div className={css("active-release-details-container flex flex-column", this.props.releaseClass)} style={containerStyle}>
                    {this._getReleaseNameElement()}
                </div>

            </div>
        );
    }

    private _getReleaseCreatorDetails(release: Release): JSX.Element {
        return (
            <Tooltip text={release.createdBy.displayName} showOnFocus={true}>
                <div className="active-release-user-container" data-is-focusable={true}>
                    <img
                        src={getIdentityImageUrl(this.context.pageContext, release.createdBy)}
                        className="active-release-identity-image"
                        alt={release.createdBy.displayName} />
                </div>
            </Tooltip>
        );
    }

    private _getReleaseNameElement(): JSX.Element {
        let releaseUrl: string = "";
        if (this.props.release.status === ReleaseStatus.Draft) {
            releaseUrl = createUrlForRoute(this.context.pageContext, "ms.vss-releaseManagement-web.draft-release-route", {
                "_a": "release-environments-editor",
                "releaseId": this.props.release.id.toString(10)
            });
        }
        else {
            releaseUrl = createUrlForRoute(this.context.pageContext, "ms.vss-releaseManagement-web.cd-release-progress-default-route", {
                "_a": "release-pipeline-progress",
                "releaseId": this.props.release.id.toString(10)
            });
        }

        return (
            <div className="release-name-element flex-center">
                {this._getReleaseName(releaseUrl)}
                {this._getReleaseSubtext(releaseUrl)}
            </div>
        );
    }

    private _getReleaseName(releaseUrl: string): JSX.Element {
        let release: Release = this.props.release;
        let releaseNameTooltipText = release.name;
        let releaseName: JSX.Element;
        let releaseStatus: React.ReactNode = null;

        this._releaseNameWidth = this._adjustWidthToFitActionButton(this._releaseNameContainer, this._releaseNameWidth);
        const releaseNameWidthStyle = {
            "maxWidth": this._releaseNameWidth
        };

        if (this.props.isDeleted) {
            releaseName =
                (<span className="active-release-name releases-isdeleted">{release.name}
                </span>
                );
        }
        else if (release.status === ReleaseStatus.Draft && !this._canViewDraftRelease()) {
            releaseName =
                (<span className="active-release-name">{release.name}
                </span>
                );
        }
        else {
            releaseName = (<FPSLink
                href={releaseUrl}
                rel="noopener noreferrer"
                className="active-release-name active-release-url"
                telemetrySource={"releases-list-name"}>
                {release.name}
            </FPSLink>);
        }

        let releaseStatusText: string | null = null;
        if (release.status === ReleaseStatus.Draft) {
            releaseStatusText = Resources.DraftStatusInfo;
        }
        else if (release.status === ReleaseStatus.Abandoned) {
            releaseStatusText = Resources.AbandonedStatusInfo;
        }

        if (!!releaseStatusText) {
            releaseStatus = <span className="active-release-status-text">{releaseStatusText}</span>;
            releaseNameTooltipText = localeFormat("{0} {1}", release.name, releaseStatusText);
        }

        return (
            <div className={"release-name-with-menu flex"}>
                {
                    this.props.release.keepForever &&
                    <Tooltip text={Resources.RetainIndefinitelyIconTooltip}                    >
                        <div className={"retention-icon"}>
                            <Icon iconName="Lock" aria-label={Resources.RetainIndefinitelyIconTooltip} />
                        </div>
                    </Tooltip>
                }
                <div
                    ref={(element) => this._releaseNameContainer = element}
                    style={releaseNameWidthStyle}
                    className={"release-name-container"}>
                    <Tooltip text={releaseNameTooltipText} overflowOnly={true}>
                        <div className={" overflow-text-container"}>
                            {releaseName}
                            {releaseStatus}
                        </div>
                    </Tooltip>
                </div>
            </div>
        );
    }

    private _canViewDraftRelease(): boolean {
        const canViewDraftRelease = hasRmUIPermissions(this.context.pageContext, ReleaseManagementUISecurityPermissions.ViewLegacyUI);
        return canViewDraftRelease;
    }

    private _getReleaseSubtext(releaseUrl: string): JSX.Element {
        const subtextContainerWidthStyle = {
            "width": this._branchNameMaxWidth + this._artifactNameMaxWidth + 24 // 10px is the right margin on each of artifact and branch elements, and 2px of icon margin each
        };

        return (
            <div className="active-release-subtext-container flex" style={subtextContainerWidthStyle}>
                {this._getPrimaryArtifactBuild()}
                {this._getPrimaryArtifactBranch()}
            </div>
        );
    }

    private _getPrimaryArtifactBuild(): React.ReactNode {
        let release: Release = this.props.release;
        let primaryArtifact: Artifact | undefined = getPrimaryArtifact(release.artifacts); // primaryArtifact could be null
        let artifactName: string = getArtifactBuildInfo(primaryArtifact);
        let artifactVersionUrl: string = getArtifactVersionUrl(this.context.pageContext, primaryArtifact);

        if (!primaryArtifact || !artifactName) {
            return null;
        }

        if (this._branchNameMaxWidth === 0) {
            // Adjust the artifact name width on hover or focus only if branch name is not present
            this._artifactNameMaxWidth = this._adjustWidthToFitActionButton(this._artifactContainer, this._artifactNameMaxWidth);
        }

        const primaryArtifactBuildWidthStyle = {
            "maxWidth": this._artifactNameMaxWidth > 50 ? this._artifactNameMaxWidth - 14 : this._artifactNameMaxWidth
        };

        return (
            <div className="active-release-subtext flex flex-center">

                {this._getArtifactIcon(primaryArtifact)}
                <div
                    ref={(element) => this._artifactContainer = element}
                    style={primaryArtifactBuildWidthStyle}
                    className={"artifact-version-container overflow-text-container"}>
                    <Tooltip text={artifactName}>
                        {this._getPrimaryArtifactNameElement(artifactName, artifactVersionUrl, primaryArtifact.type)}
                    </Tooltip>
                </div>
            </div>
        );
    }

    private _getArtifactIcon(artifact: Artifact | undefined) {
        if (!artifact) {
            return null;
        }

        let artifactIcon: string = getArtifactIcon(artifact.type);

        return (
            <Icon iconName={artifactIcon} aria-label={artifact.type} className="artifact-type-icon" />
        );
    }

    private _getPrimaryArtifactNameElement(artifactName: string, artifactVersionUrl: string, artifactType: string): React.ReactNode {
        let elm: React.ReactNode = null;
        if (artifactVersionUrl) {
            const LinkComponentClass: any = artifactType === ArtifactTypes.BuildArtifactType ? FPSLink : Link;
            elm = (
                <LinkComponentClass
                    href={artifactVersionUrl}
                    rel="noopener noreferrer"
                    className="light-text active-release-artifact-url"
                    role={artifactVersionUrl ? "link" : "button"}
                    removeUnderline={true}
                >
                    {artifactName}

                </LinkComponentClass>
            );
        }
        else {
            elm = (
                <span className="light-text"> {artifactName} </span>
            );
        }

        return elm;
    }


    private _getPrimaryArtifactBranch(): React.ReactNode {
        let release: Release = this.props.release;
        let branchName: string = getPrimaryArtifactBranchName(release.artifacts);
        let branchDisplayName: string = getBranchDisplayValue(branchName);

        if (!branchDisplayName) {
            return null;
        }

        this._branchNameMaxWidth = this._adjustWidthToFitActionButton(this._branchContainer, this._branchNameMaxWidth);
        const primaryArtifactBranchWidthStyle = {
            "maxWidth": this._branchNameMaxWidth > 50 ? this._branchNameMaxWidth - 14 : this._branchNameMaxWidth // For medium to large sized branch names remove space alloted for icon
        };

        return (
            <div className="active-release-subtext flex flex-center">
                <Icon
                    iconName={"OpenSource"}
                    aria-label={Resources.BranchAriaLabel}
                    className="active-release-branch-icon" />
                <div
                    ref={(element) => this._branchContainer = element}
                    style={primaryArtifactBranchWidthStyle}
                    className={"branch-name-container overflow-text-container"}>
                    <Tooltip text={branchDisplayName}>
                        <span className="light-text"> {branchDisplayName} </span>
                    </Tooltip>
                </div>
            </div>
        );
    }

    private _adjustWidthToFitActionButton(container: HTMLDivElement | null, currentWidth: number): number {
        if (container && (this.state.focusedRowReleaseId === this.props.release.id || this.state.hoveredRowReleaseId === this.props.release.id)) {
            if (currentWidth > 40) {
                return currentWidth - 40;
            }
            else {
                return currentWidth;
            }
        }
        else {
            return currentWidth;
        }
    }

    private _setElementWidthsFromProps(props: IActiveReleaseNameProps) {
        this._releaseDetailsContainerWidth = props.columnWidth > 100 ? props.columnWidth : 100;
        this._artifactNameMaxWidth = props.artifactNameMaxWidth;
        this._branchNameMaxWidth = props.branchNameMaxWidth;
        this._releaseNameWidth = this._releaseDetailsContainerWidth - avatarImageWidth;
    }

    private _onStoreUpdate = (): void => {
        const newHoveredId = this._store.getHoveredReleaseId();
        const newFocusedId = this._store.getFocusedReleaseId();
        const releaseId = this.props.release.id;
        if ((newHoveredId === releaseId && this.state.hoveredRowReleaseId !== newHoveredId) /* hovered on this row from another row */
            || this.state.hoveredRowReleaseId === releaseId /* hovered away from this row */
            || (newFocusedId === releaseId && this.state.focusedRowReleaseId != newFocusedId) /* focused on this row from another row */
            || this.state.focusedRowReleaseId === releaseId /* focused away from this row */) {

            this.setState({
                focusedRowReleaseId: newFocusedId,
                hoveredRowReleaseId: newHoveredId
            });
        }
    }

    private _releaseNameWidth: number;
    private _releaseDetailsContainerWidth: number;
    private _artifactNameMaxWidth: number;
    private _branchNameMaxWidth: number;
    private _store: ActiveReleasesStore;
    private _releaseNameContainer: HTMLDivElement | null;
    private _artifactContainer: HTMLDivElement | null;
    private _branchContainer: HTMLDivElement | null;
}